/**
 * Credential Validation Primitives
 *
 * v0.5.0 ships the storage-layer prompt-leak guard only. Full key-shape
 * validation (GitHub PAT family, Slack, GitLab, NPM, etc.) and the
 * --force-shape escape hatch are scoped for v0.5.1.
 *
 * The existing wizard-side regex table at src/wizard/validation.ts is
 * unchanged for v0.5.0. v0.5.1 will move it here and wire it through
 * all capture paths.
 */

/**
 * Escape a string for use as a literal inside a RegExp.
 */
function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Detect whether a value has the shape of the pre-v0.5.0 keyValue()
 * formatter output for the given key name: leading whitespace (0-4 spaces),
 * the key name, a colon, optional whitespace, then the actual value.
 *
 * Returns the stripped value alongside the leak flag so callers can choose
 * to reject (recommended; storage layer does this) or repair.
 *
 * Example match:
 *   "  GH_PAT_TEST: ghp_AAAA..." -> { leak: true, stripped: "ghp_AAAA..." }
 */
export function detectPromptLeak(
  name: string,
  value: string
): { leak: boolean; stripped: string } {
  const re = new RegExp(`^\\s{0,4}${escapeRegExp(name)}\\s*:\\s+`);
  if (re.test(value)) {
    return { leak: true, stripped: value.replace(re, '') };
  }
  return { leak: false, stripped: value };
}
